import { createORPCClient } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { apiFetch } from "@packages/core-client/api/apiFetch.ts";
import { invalidate, queryPath } from "@packages/core-client/query/queryKeys.ts";
import { useEnvironmentVariable } from "@squide/firefly";
import { useMemo } from "react";
import { managePeopleContract } from "#shared/managePeopleContract.ts";

function createManagePeopleClient(baseUrl: string) {
    const link = new OpenAPILink(managePeopleContract, {
        url: `${baseUrl}/api/manage-people`,
        fetch: apiFetch
    });

    const client: ContractRouterClient<typeof managePeopleContract> = createORPCClient(link);
    const queryUtils = createTanstackQueryUtils(client, { path: queryPath("admin-settings", ["members", "departments", "job-titles", "provisioning"]) });

    return { client, queryUtils };
}

export function useManagePeopleClient() {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");

    return useMemo(() => createManagePeopleClient(apiBaseUrl), [apiBaseUrl]);
}

export function useManagePermissionsMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.managePermissions.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useUpdatePersonJobInformationMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.updatePersonJobInformation.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, ["members", "org-chart"])
    });
}

export function useAddPersonEmploymentEntryMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.addPersonEmploymentEntry.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, variables, _onMutateResult, { client: queryClient }) => {
            void queryClient.invalidateQueries({ queryKey: queryUtils.listPersonEmploymentHistory.key({ input: { personId: variables.personId } }) });
            void invalidate(queryClient, "members");
        }
    });
}

export function useUpdatePersonEmploymentEntryMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.updatePersonEmploymentEntry.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, variables, _onMutateResult, { client: queryClient }) => {
            void queryClient.invalidateQueries({ queryKey: queryUtils.listPersonEmploymentHistory.key({ input: { personId: variables.personId } }) });
            void invalidate(queryClient, "members");
        }
    });
}

export function useRemovePersonEmploymentEntryMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.removePersonEmploymentEntry.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, variables, _onMutateResult, { client: queryClient }) => {
            void queryClient.invalidateQueries({ queryKey: queryUtils.listPersonEmploymentHistory.key({ input: { personId: variables.personId } }) });
            void invalidate(queryClient, "members");
        }
    });
}

export function useUpdatePersonMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.updatePerson.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useUpdatePersonCustomPropertiesMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.updatePersonCustomProperties.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useUpdatePersonPhotoMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.updatePersonPhoto.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useRemovePersonPhotoMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.removePersonPhoto.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useDeactivatePersonMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.deactivatePerson.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useReactivatePersonMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.reactivatePerson.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useSendInviteMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.sendInvite.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useResendInviteMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.resendInvite.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useRemovePersonMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.removePerson.mutationOptions({
        meta: { skipGlobalErrorHandler: true },
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useDeactivatePeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.deactivatePeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useReactivatePeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.reactivatePeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useSendInvitePeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.sendInvitePeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useResendInvitePeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.resendInvitePeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useRemovePeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.removePeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useInvitePeopleByEmailMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.invitePeopleByEmail.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useCreatePeopleWithoutInviteMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.createPeopleWithoutInvite.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useCreateNonSeatPeopleMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.createNonSeatPeople.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useInvitePeopleByTextMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.invitePeopleByText.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}

export function useCreatePeopleWithoutInviteByTextMutationOptions() {
    const { queryUtils } = useManagePeopleClient();

    return queryUtils.createPeopleWithoutInviteByText.mutationOptions({
        onSuccess: (_data, _variables, _onMutateResult, { client: queryClient }) => invalidate(queryClient, "members")
    });
}
